import * as React from "react";
import { cn } from "@/lib/utils";

interface TimelineItem {
  icon: React.ReactNode;
  title: string;
  description?: string;
  timestamp: string | Date;
  color?: "primary" | "success" | "warning" | "danger";
}

interface ActivityTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  items: TimelineItem[];
  maxItems?: number;
}

const colorClasses = {
  primary: {
    bg: "bg-primary/10",
    border: "border-primary",
    text: "text-primary",
  },
  success: {
    bg: "bg-success/10",
    border: "border-success",
    text: "text-success",
  },
  warning: {
    bg: "bg-warning/10",
    border: "border-warning",
    text: "text-warning",
  },
  danger: {
    bg: "bg-danger/10",
    border: "border-danger",
    text: "text-danger",
  },
};

function formatTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "الآن";
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
  if (diffHours < 24) return `منذ ${diffHours} ساعة`;
  if (diffDays < 7) return `منذ ${diffDays} يوم`;

  return date.toLocaleDateString("ar-SA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const ActivityTimeline = React.forwardRef<HTMLDivElement, ActivityTimelineProps>(
  ({ items, maxItems, className, ...props }, ref) => {
    const displayItems = maxItems ? items.slice(0, maxItems) : items;

    return (
      <div ref={ref} className={cn("space-y-0", className)} {...props}>
        {displayItems.map((item, index) => {
          const color = item.color || "primary";
          const colorClass = colorClasses[color];
          const isLast = index === displayItems.length - 1;

          return (
            <div key={index} className="relative flex gap-4">
              {/* Timeline Line */}
              {!isLast && (
                <div className="absolute right-5 top-10 bottom-0 w-px bg-border" />
              )}

              {/* Icon */}
              <div
                className={cn(
                  "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2",
                  colorClass.bg,
                  colorClass.border
                )}
              >
                <div className={cn("h-5 w-5", colorClass.text)}>{item.icon}</div>
              </div>

              {/* Content */}
              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{item.title}</p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <time className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(item.timestamp)}
                  </time>
                </div>
              </div>
            </div>
          );
        })}

        {maxItems && items.length > maxItems && (
          <p className="text-sm text-muted-foreground text-center pt-2">
            + {items.length - maxItems} أحداث أخرى
          </p>
        )}
      </div>
    );
  }
);
ActivityTimeline.displayName = "ActivityTimeline";

export { ActivityTimeline };
export type { ActivityTimelineProps, TimelineItem };
